import { useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import { filterValuesSeparator } from 'shared-helpers/consts';
import api from 'api';
import { actions } from 'store';
import {
  ConfirmButton,
  ConfirmDeleteButton,
  ModalButtonWithForm
} from 'components/common';
import { useBoxDispatch } from 'components/common/box';
import { FormGroup, Text } from 'components/common/form';
import { UsersListSelectedUsers } from 'components/users';

import type { GetCalendarEventUsersRes } from 'shared-types/responses';

interface Props {
  selectedUserIds?: number[];
  status: 'added' | 'queued';
  updateUsers: () => Promise<void>;
  user?: GetCalendarEventUsersRes['users'][0];
}

const CalendarEventParticipantsRemove: React.FunctionComponent<Props> = ({
  selectedUserIds,
  status,
  updateUsers,
  user
}) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { calendarEvent },
    serverConfig: { requireReasonWhenDeletingData }
  } = useReduxState();

  const boxContextDispatch = useBoxDispatch();

  const [userIds, setUserIds] = useState<number[]>([]);

  const handleRemove = async (targetUrl: string) => {
    await api.delete(targetUrl);
    await updateUsers();
  };

  const onConfirm = async (values?: { deletionReason?: string }) => {
    let url = `/admin/calendar-events/${calendarEvent.id}/users${
      user ? `/${user.id}` : ''
    }?status=${status}`;

    if (values?.deletionReason) {
      url += `&deletionReason=${encodeURIComponent(values.deletionReason)}`;
    }

    await handleRemove(url);
    return true;
  };

  const onSelectedSubmit = async (values?: { deletionReason?: string }) => {
    let url = `/admin/calendar-events/${
      calendarEvent.id
    }/users?userIds=${userIds.join(filterValuesSeparator)}&status=${status}`;

    if (values?.deletionReason) {
      url += `&deletionReason=${encodeURIComponent(values.deletionReason)}`;
    }

    await handleRemove(url);
    boxContextDispatch({ type: 'resetSelection' });
    dispatch(
      actions.addNotice({
        text: messages.commonRemoveUsersSelectedSuccess,
        type: 'success'
      })
    );
  };

  return selectedUserIds?.length ? (
    <ModalButtonWithForm
      buttonCategory="danger"
      buttonIcon="trash-alt"
      buttonIsDropdown={true}
      buttonText={messages.commonRemoveUsersSelected}
      modalActionText={messages.commonRemove}
      modalTitle={messages.commonRemoveUsersSelected}
      modalWidth="medium"
      onSubmit={onSelectedSubmit}
    >
      {() => (
        <>
          <UsersListSelectedUsers
            allowGuestUsers={true}
            allowSelf={true}
            onLoadedUsers={(users) => setUserIds(users.map((user) => user.id))}
            userSelectionParams={{ selectedUserIds }}
          />

          <div style={{ marginTop: 20 }}>
            {messages.commonRemoveUsersSelectedConfirm}
          </div>

          {requireReasonWhenDeletingData ? (
            <FormGroup
              htmlFor="deletionReasonParticipantsSelected"
              label={messages.commonDeletionReason}
              style={{ marginTop: 20 }}
            >
              <Text
                field="deletionReason"
                id="deletionReasonParticipantsSelected"
                maxLength={255}
                autocomplete="off"
                placeholder={messages.commonInputDeletionReason}
                required={true}
              />
            </FormGroup>
          ) : null}
        </>
      )}
    </ModalButtonWithForm>
  ) : requireReasonWhenDeletingData ? (
    <ConfirmDeleteButton
      buttonIsDropdown={true}
      onConfirm={onConfirm}
      {...(user
        ? {
            buttonText: messages.commonRemoveUser,
            confirmText: messages.commonRemoveUserConfirm,
            confirmTitle: messages.commonRemoveUser,
            id: 'deletionReasonParticipant',
            successMessage: messages.commonRemoveUserSuccess
          }
        : {
            buttonText: messages.commonRemoveAllUsers,
            confirmText: messages.settingsRemoveAllUsersConfirm,
            confirmTitle: messages.commonRemoveAllUsers,
            id: 'deletionReasonParticipants',
            successMessage: messages.commonRemoveAllUsersSuccess
          })}
    />
  ) : (
    <ConfirmButton
      actionText={messages.commonRemove}
      buttonIcon="trash-alt"
      buttonIsDropdown={true}
      category="danger"
      confirmText={messages.commonRemoveUserConfirm}
      onConfirm={onConfirm}
      {...(user
        ? {
            buttonText: messages.commonRemoveUser,
            confirmText: messages.commonRemoveUserConfirm,
            confirmTitle: messages.commonRemoveUser,
            successMessage: messages.commonRemoveUserSuccess
          }
        : {
            buttonText: messages.commonRemoveAllUsers,
            confirmText: messages.settingsRemoveAllUsersConfirm,
            confirmTitle: messages.commonRemoveAllUsers,
            successMessage: messages.commonRemoveAllUsersSuccess
          })}
    />
  );
};

export default CalendarEventParticipantsRemove;
